import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight,
  FaMobileAlt,
  FaRobot,
  FaGlobe,
  FaServer,
  FaStar,
  FaShieldAlt,
  FaClock,
  FaLayerGroup,
  FaExternalLinkAlt
} from 'react-icons/fa';
import '../css/Projects.css';

import cutcutLanding from '../assets/cutcut/landing.png';
import cutcutDashboard from '../assets/cutcut/dashboard.png';
import cutcutAdminDashboard from '../assets/cutcut/admin-dashboard.png';
import cutcutPayments from '../assets/cutcut/payments.png';
import cutcutServices from '../assets/cutcut/services.png';
import cutcutTestimonial from '../assets/cutcut/testimonial.png';
import batmanincImage from '../assets/projects/batmaninc.png';
import zergroupImage from '../assets/projects/zergroup.png';
import wikywingsImage from '../assets/projects/wikywings.png';
import clinicImage from '../assets/projects/clinic.png';

const mtbFabricsImage = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="1400" height="900" viewBox="0 0 1400 900">
  <defs>
    <linearGradient id="bg" x1="0" x2="1" y1="0" y2="1">
      <stop offset="0" stop-color="#101827"/>
      <stop offset="0.48" stop-color="#123d46"/>
      <stop offset="1" stop-color="#0f172a"/>
    </linearGradient>
    <linearGradient id="fabric" x1="0" x2="1">
      <stop offset="0" stop-color="#67e8f9"/>
      <stop offset="0.5" stop-color="#f8d77e"/>
      <stop offset="1" stop-color="#c084fc"/>
    </linearGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="18" result="blur"/>
      <feMerge>
        <feMergeNode in="blur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
  </defs>
  <rect width="1400" height="900" fill="url(#bg)"/>
  <g opacity="0.18">
    <path d="M0 120H1400M0 240H1400M0 360H1400M0 480H1400M0 600H1400M0 720H1400" stroke="#fff"/>
    <path d="M120 0V900M280 0V900M440 0V900M600 0V900M760 0V900M920 0V900M1080 0V900M1240 0V900" stroke="#fff"/>
  </g>
  <g filter="url(#glow)" opacity="0.9">
    <path d="M110 620C260 440 350 720 520 500C700 260 820 640 980 420C1110 245 1240 300 1330 230" fill="none" stroke="url(#fabric)" stroke-width="34" stroke-linecap="round"/>
    <path d="M70 710C250 550 430 780 590 610C790 400 860 710 1090 520C1200 430 1285 470 1360 400" fill="none" stroke="#ffffff" stroke-width="4" stroke-linecap="round" opacity="0.55"/>
  </g>
  <rect x="120" y="105" width="1160" height="560" rx="34" fill="rgba(255,255,255,0.07)" stroke="rgba(255,255,255,0.22)"/>
  <text x="170" y="205" fill="#e0fbff" font-family="Inter, Arial" font-size="34" font-weight="800" letter-spacing="4">MTB FABRICS</text>
  <text x="170" y="290" fill="#ffffff" font-family="Inter, Arial" font-size="86" font-weight="900">Premium Fabric</text>
  <text x="170" y="385" fill="#ffffff" font-family="Inter, Arial" font-size="86" font-weight="900">Business Website</text>
  <text x="174" y="470" fill="#bae6fd" font-family="Inter, Arial" font-size="32">Catalog-ready visual system, responsive pages, and brand presentation.</text>
  <g transform="translate(170 540)">
    <rect width="170" height="54" rx="10" fill="#67e8f9"/>
    <text x="28" y="36" fill="#06111d" font-family="Inter, Arial" font-size="22" font-weight="900">WEB BUILD</text>
    <rect x="195" width="152" height="54" rx="10" fill="rgba(255,255,255,0.11)" stroke="rgba(255,255,255,0.2)"/>
    <text x="226" y="36" fill="#ffffff" font-family="Inter, Arial" font-size="22" font-weight="800">BRAND UI</text>
  </g>
</svg>
`)}`;

const projectData = [
  {
    name: 'Mechanic Finders App',
    description:
      'An on-demand service experience for vehicle owners to book jobs, manage cars, and track appointments through a polished customer and admin system.',
    longDescription:
      'Mechanic Finders turns a mechanic business into a complete digital service experience, covering bookings, vehicle records, scheduling, authentication, and operational control in one connected product.',
    link: 'https://oz-mechanic.shop',
    category: 'Mobile',
    imageUrl: 'https://i.ibb.co/PzYY9MdG/landing.png',
    tech: ['React Native', 'FastAPI', 'MongoDB', 'JWT', 'Admin Dashboard'],
    stats: [
      { label: 'Platform', value: 'Customer + Admin' },
      { label: 'Type', value: 'Service product' },
      { label: 'Focus', value: 'Bookings + vehicles' }
    ],
    accent: 'gold'
  },
  {
    name: 'CutCut Barbershop App',
    description:
      'A premium barber booking experience with customer flows, admin modules, payments, and polished mobile UI.',
    longDescription:
      'CutCut Barbershop is a premium booking platform for modern grooming businesses, combining customer booking journeys, account dashboards, admin control, payment management, and a polished mobile-first experience.',
    link: 'https://testflight.apple.com/join/MV1AuVHc',
    category: 'Mobile',
    imageUrl: cutcutLanding,
    gallery: [
      cutcutLanding,
      cutcutDashboard,
      cutcutAdminDashboard,
      cutcutPayments,
      cutcutServices,
      cutcutTestimonial
    ],
    tech: ['React Native', 'Booking UX', 'Admin Dashboard', 'Payments'],
    stats: [
      { label: 'Platform', value: 'Customer + Admin' },
      { label: 'Type', value: 'Booking product' },
      { label: 'Focus', value: 'Appointments + payments' }
    ],
    accent: 'blue'
  },
  {
  name: 'BatmanInc Website',
  description:
    'A construction-focused website with strong visual hero sections and clean service presentation.',
  longDescription:
    'BatmanInc is a polished construction website focused on trust, presentation, and modern visual storytelling for the brand.',
  link: 'https://www.batmaninc.ca/',
  category: 'Web',
  imageUrl: batmanincImage,
  tech: ['React', 'Responsive UI', 'Business Website'],
  stats: [
    { label: 'Platform', value: 'Business website' },
    { label: 'Type', value: 'Construction' },
    { label: 'Focus', value: 'Brand + services' }
  ],
  accent: 'gold'
},
{
  name: 'Zergroup Website',
  description:
    'A modern corporate construction-style website with project-focused presentation and premium dark visual direction.',
  longDescription:
    'Zergroup is designed as a clean and premium corporate web experience with project highlights, service navigation, and strong hero presentation.',
  link: 'https://www.zergroup.ca/',
  category: 'Web',
  imageUrl: zergroupImage,
  tech: ['React', 'Corporate UI', 'Project Showcase'],
  stats: [
    { label: 'Platform', value: 'Corporate website' },
    { label: 'Type', value: 'Construction / business' },
    { label: 'Focus', value: 'Projects + branding' }
  ],
  accent: 'blue'
},
{
  name: 'WikyWings Website',
  description:
    'A restaurant landing experience with warm branding, polished typography, and strong visual presentation.',
  longDescription:
    'WikyWings is a premium restaurant website built to combine atmosphere, food presentation, and strong modern branding into one memorable digital experience.',
  link: 'https://www.wikywings.ca/',
  category: 'Web',
  imageUrl: wikywingsImage,
  tech: ['React', 'Restaurant UI', 'Brand Design'],
  stats: [
    { label: 'Platform', value: 'Restaurant website' },
    { label: 'Type', value: 'Food brand' },
    { label: 'Focus', value: 'Atmosphere + menu' }
  ],
  accent: 'purple'
},
{
  name: 'Park Clinic Website',
  description:
    'A clinic website with campaign-driven landing sections, strong CTA structure, and conversion-focused healthcare presentation.',
  longDescription:
    'Park Clinic is a healthcare-focused website built with strong conversion structure, promotional campaign sections, and a modern clinic brand presentation.',
  link: 'https://www.parkclinic.health/en',
  category: 'Web',
  imageUrl: clinicImage,
  tech: ['React', 'Healthcare UI', 'Landing Page'],
  stats: [
    { label: 'Platform', value: 'Clinic website' },
    { label: 'Type', value: 'Healthcare' },
    { label: 'Focus', value: 'CTA + conversion' }
  ],
  accent: 'cyan'
},
{
  name: 'MTB Fabrics Website',
  description:
    'A fabric business website focused on brand trust, product presentation, responsive structure, and clean customer navigation.',
  longDescription:
    'MTB Fabrics is a business web project built around a polished visual system, clear product presentation, mobile-friendly structure, and a professional first impression for customers exploring fabric services and inventory.',
  link: 'https://www.mtbfabrics.com/',
  category: 'Web',
  imageUrl: mtbFabricsImage,
  tech: ['React', 'Business Website', 'Responsive UI', 'Brand System'],
  stats: [
    { label: 'Platform', value: 'Business website' },
    { label: 'Type', value: 'Fabric / textile brand' },
    { label: 'Focus', value: 'Products + trust' }
  ],
  accent: 'cyan'
},
  {
    name: 'Sword Garage Mechanic',
    description:
      'A multilingual booking website crafted to present automotive services with trust, clarity, and strong conversion-focused design.',
    longDescription:
      'Sword Garage combines service presentation, multilingual content, and booking flow design to create a stronger online presence for an automotive business.',
    link: 'https://swordgarage.com',
    category: 'Web',
    imageUrl: 'https://i.ibb.co/KjmY6F86/Screenshot-2025-02-05-at-2-49-03-PM.png',
    tech: ['React', 'i18n', 'Reusable UI'],
    stats: [
      { label: 'Platform', value: 'Business website' },
      { label: 'Type', value: 'Web product' },
      { label: 'Focus', value: 'Trust + booking' }
    ],
    accent: 'purple'
  },
  {
    name: 'Voice Clone AI',
    description:
      'An AI experience centered on identity verification and realistic voice generation for fast, interactive user flows.',
    longDescription:
      'Voice Clone AI combines biometric checks and voice synthesis into a sharp, user-facing product that feels fast, modern, and highly interactive.',
    link: 'https://www.oz-ai.xyz/',
    category: 'AI',
    imageUrl: 'https://i.ibb.co/KpKfy5QW/Screenshot-2025-05-11-at-6-57-43-AM.png',
    tech: ['React', 'FastAPI', 'Voice AI'],
    stats: [
      { label: 'Platform', value: 'AI product' },
      { label: 'Type', value: 'Verification + voice' },
      { label: 'Focus', value: 'Biometric flows' }
    ],
    accent: 'cyan'
  },
  {
    name: 'Ramiz Kara Eski – Personal AI Assistant',
    description:
      'A desktop-oriented assistant that connects messaging, scheduling, utilities, and local intelligence into one practical tool.',
    longDescription:
      'Ramiz Kara Eski is built around useful daily actions — from calendar and email support to local AI-assisted commands — making it feel more like a working companion than a static demo.',
    link: 'https://www.ramizkaraeskigpt.xyz/',
    category: 'AI',
    imageUrl: 'https://i.ibb.co/Nd2V3Yrs/Screenshot-2025-05-11-at-6-59-34-AM.png',
    tech: ['Assistant UX', 'Automation', 'Smart Actions'],
    stats: [
      { label: 'Platform', value: 'Desktop assistant' },
      { label: 'Type', value: 'AI utility' },
      { label: 'Focus', value: 'Daily workflows' }
    ],
    accent: 'violet'
  },
  {
    name: 'Code AI Tester',
    description:
      'A developer tool that reviews uploaded code, surfaces issues, and assists with documentation, tests, and improvement suggestions.',
    longDescription:
      'Code AI Tester is designed to reduce engineering friction by turning code uploads into structured feedback, test ideas, documentation help, and analysis support.',
    link: 'https://www.ozaicodetester.services/',
    category: 'AI',
    imageUrl: 'https://i.ibb.co/qLt630Xs/Screenshot-2025-05-11-at-8-12-35-AM.png',
    tech: ['LLM Workflows', 'Code Analysis', 'Developer Tools'],
    stats: [
      { label: 'Platform', value: 'Developer tool' },
      { label: 'Type', value: 'AI analysis' },
      { label: 'Focus', value: 'Code feedback' }
    ],
    accent: 'pink'
  },
  {
    name: 'TrioTeach Backend ERP',
    description:
      'A management-focused backend handling invoices, internal operations, and structured business logic.',
    longDescription:
      'This ERP-oriented build focuses on the core backend layer behind internal business processes, with an emphasis on structure, scalability, and clean data handling.',
    link: 'https://github.com/ozXoz/TrioTeach-Backend-ERP',
    category: 'Backend',
    imageUrl: 'https://i.ibb.co/47Pg1m5/Screenshot-2025-01-04-at-5-03-56-PM.png',
    tech: ['Node.js', 'Express.js', 'MongoDB'],
    stats: [
      { label: 'Platform', value: 'Backend system' },
      { label: 'Type', value: 'ERP logic' },
      { label: 'Focus', value: 'Operations' }
    ],
    accent: 'blue'
  },
  {
    name: 'Nexyra Teach SaaS',
    description:
      'A business management system covering inventory, reporting, and operational visibility in one organized experience.',
    longDescription:
      'Nexyra Teach SaaS brings together reporting, operations, and management features in a way that helps teams understand activity and control business flow more clearly.',
    link: 'https://www.nexyramanagement.ltd/',
    category: 'Web',
    imageUrl: 'https://i.ibb.co/TYDmkxs/Screenshot-2025-01-04-at-5-09-32-PM.png',
    tech: ['Full Stack', 'Reporting', 'Management'],
    stats: [
      { label: 'Platform', value: 'Business SaaS' },
      { label: 'Type', value: 'Management software' },
      { label: 'Focus', value: 'Reporting + control' }
    ],
    accent: 'cyan'
  }
];

const filters = ['All', 'AI', 'Mobile', 'Web', 'Backend'];

const getCategoryIcon = (category) => {
  switch (category) {
    case 'AI':
      return <FaRobot />;
    case 'Mobile':
      return <FaMobileAlt />;
    case 'Web':
      return <FaGlobe />;
    case 'Backend':
      return <FaServer />;
    default:
      return <FaStar />;
  }
};

const revealUp = {
  hidden: { opacity: 0, y: 35 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] }
  }
};

const cardPop = {
  hidden: { opacity: 0, y: 40, scale: 0.97 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.06,
      duration: 0.65,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};

export default function Projects() {
  const [filter, setFilter] = useState('All');

  const filteredProjects = useMemo(() => {
    return filter === 'All'
      ? projectData
      : projectData.filter((project) => project.category === filter);
  }, [filter]);

  const featuredProject = filteredProjects[0];
  const gridProjects = filteredProjects.slice(1);

  return (
    <section id="projects" className="projects-section">
      <div className="projects-bg-orb orb-left" />
      <div className="projects-bg-orb orb-right" />
      <div className="projects-mesh" />
      <div className="projects-watermark">SHOWCASE</div>

      <motion.div
        className="projects-heading-wrap"
        variants={revealUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
      >
        <span className="projects-kicker">Selected Work</span>
        <h1>Projects</h1>
        <p>
          Premium builds across mobile apps, AI systems, backend platforms,
          service products, and business-focused software.
        </p>
      </motion.div>

      <motion.div
        className="project-filter-wrap"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        {filters.map((item) => (
          <button
            key={item}
            type="button"
            className={`filter-pill ${filter === item ? 'active' : ''}`}
            onClick={() => setFilter(item)}
          >
            <span className="filter-pill-icon">
              {item === 'All' ? <FaStar /> : getCategoryIcon(item)}
            </span>
            <span>{item}</span>
          </button>
        ))}
      </motion.div>

      <AnimatePresence mode="wait">
        <motion.div
          key={filter}
          className="projects-shell"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 18 }}
          transition={{ duration: 0.4 }}
        >
          {featuredProject && (
            <motion.a
              href={featuredProject.link}
              target="_blank"
              rel="noopener noreferrer"
              className={`projects-hero-card accent-${featuredProject.accent}`}
              variants={cardPop}
              initial="hidden"
              animate="show"
              whileHover={{
                y: -8,
                rotateX: 1.5,
                rotateY: -1.5,
                transition: { duration: 0.22 }
              }}
            >
              <div className="hero-card-noise" />
              <div className="hero-card-shine" />

              <div className="hero-card-left">
                <div className="hero-card-top">
                  <span className="hero-category-pill">
                    {getCategoryIcon(featuredProject.category)}
                    {featuredProject.category}
                  </span>

                  <span className="hero-small-badge">
                    <FaShieldAlt />
                    Production-minded
                  </span>
                </div>

                <h2>{featuredProject.name}</h2>
                <p className="hero-description">{featuredProject.longDescription}</p>

                <div className="hero-tech-row">
                  {featuredProject.tech.map((item, index) => (
                    <motion.span
                      key={item}
                      className="hero-tech-pill"
                      initial={{ opacity: 0, scale: 0.92 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.18 + index * 0.05, duration: 0.25 }}
                      whileHover={{ y: -2 }}
                    >
                      {item}
                    </motion.span>
                  ))}
                </div>

                <div className="hero-metric-rail">
                  {featuredProject.stats.map((stat) => (
                    <div key={stat.label} className="hero-metric-box">
                      <span>{stat.label}</span>
                      <strong>{stat.value}</strong>
                    </div>
                  ))}
                </div>

                <div className="hero-bottom">
                  <div className="hero-action">
                    <span>Explore Project</span>
                    <FaExternalLinkAlt />
                  </div>

                  <div className="hero-meta-mini">
                    <FaClock />
                    Real workflow product
                  </div>
                </div>
              </div>

              <div className="hero-card-right">
                <div className="hero-spotlight" />

                <motion.div
                  className="hero-image-panel back back-left"
                  animate={{ y: [0, -10, 0], rotate: [-8, -10, -8] }}
                  transition={{ repeat: Infinity, duration: 5.8, ease: 'easeInOut' }}
                >
                  <img
                    src={featuredProject.imageUrl}
                    alt={`${featuredProject.name} background left`}
                  />
                </motion.div>

                <motion.div
                  className="hero-image-panel back back-right"
                  animate={{ y: [0, -12, 0], rotate: [8, 10, 8] }}
                  transition={{ repeat: Infinity, duration: 6.2, ease: 'easeInOut' }}
                >
                  <img
                    src={featuredProject.imageUrl}
                    alt={`${featuredProject.name} background right`}
                  />
                </motion.div>

                <motion.div
                  className="hero-image-panel main"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 5.2, ease: 'easeInOut' }}
                >
                  <img
                    src={featuredProject.imageUrl}
                    alt={featuredProject.name}
                    className="hero-main-image"
                  />
                </motion.div>

                <motion.div
                  className="hero-floating-tag tag-one"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ repeat: Infinity, duration: 3.2, ease: 'easeInOut' }}
                >
                  <FaLayerGroup />
                  Product System
                </motion.div>

                <motion.div
                  className="hero-floating-tag tag-two"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 3.8, ease: 'easeInOut' }}
                >
                  <FaShieldAlt />
                  Secure Flows
                </motion.div>
              </div>
            </motion.a>
          )}

          <motion.div layout className="projects-grid-v2">
            <AnimatePresence mode="popLayout">
              {gridProjects.map((project, index) => (
                <motion.a
                  layout
                  key={project.name}
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`project-card-v2 accent-${project.accent}`}
                  custom={index}
                  variants={cardPop}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, y: 20, scale: 0.97 }}
                  whileHover={{
                    y: -8,
                    rotateX: 1.2,
                    rotateY: index % 2 === 0 ? -1.2 : 1.2,
                    transition: { duration: 0.22 }
                  }}
                >
                  <div className="project-card-glow" />
                  <div className="project-card-shine" />

                  <div className="project-media-shell">
                    <img
                      src={project.imageUrl}
                      alt={project.name}
                      className="project-card-image"
                    />
                  </div>

                  <div className="project-card-content">
                    <div className="project-card-meta">
                      <span className="project-type-pill">
                        {getCategoryIcon(project.category)}
                        {project.category}
                      </span>
                    </div>

                    <h3>{project.name}</h3>
                    <p>{project.description}</p>

                    <div className="project-tag-row">
                      {project.tech.slice(0, 3).map((item) => (
                        <span key={item} className="project-mini-pill">
                          {item}
                        </span>
                      ))}
                    </div>

                    <div className="project-footer-link">
                      <span>View Project</span>
                      <FaArrowRight />
                    </div>
                  </div>
                </motion.a>
              ))}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
